import React, { useState, useEffect } from 'react';

const baseStyles = {
  shell: {
    maxWidth: 600,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
  },
  card: {
    background: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '28px 28px 0',
    textAlign: 'center',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
    color: '#0f172a',
  },
  subtitle: {
    marginTop: 8,
    color: '#64748b',
    fontSize: 15,
    lineHeight: 1.5,
  },
  form: {
    padding: 24,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    resize: 'vertical',
    minHeight: 120,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fff',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  severityOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
  },
  severityBtn: {
    padding: '12px 8px',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  },
  severityLow: { borderColor: '#22c55e', color: '#16a34a' },
  severityMedium: { borderColor: '#eab308', color: '#ca8a04' },
  severityHigh: { borderColor: '#f97316', color: '#ea580c' },
  severityCritical: { borderColor: '#dc2626', color: '#b91c1c' },
  severityBtnActive: {
    background: '#f1f5f9',
  },
  submitBtn: {
    width: '100%',
    padding: '16px 24px',
    border: 'none',
    borderRadius: 12,
    background: '#dc2626',
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  success: {
    padding: 20,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 12,
    textAlign: 'center',
    color: '#166534',
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 12,
    color: '#22c55e',
  },
  error: {
    padding: 14,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 12,
    color: '#b91c1c',
    fontSize: 14,
    marginBottom: 16,
  },
  fieldError: {
    color: '#dc2626',
    fontSize: 13,
    marginTop: 6,
  },
  notice: {
    padding: 14,
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderRadius: 12,
    fontSize: 14,
    color: '#92400e',
    marginBottom: 20,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
  },
};

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('complaint-form-styles')) return;

  const style = document.createElement('style');
  style.id = 'complaint-form-styles';
  style.textContent = `
    @media (max-width: 640px) {
      .complaint-shell { padding: 14px !important; }
      .complaint-card { border-radius: 16px !important; }
      .severity-options { grid-template-columns: repeat(2, 1fr) !important; }
    }
    .complaint-input:focus, .complaint-textarea:focus, .complaint-select:focus {
      border-color: #dc2626 !important;
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;
    }
    .complaint-submit:hover { background: #b91c1c !important; }
    .complaint-submit:disabled { background: #fca5a5 !important; cursor: not-allowed !important; }
    .severity-btn.selected { background: var(--severity-color, #f1f5f9); }
  `;
  document.head.appendChild(style);
}

const complaintTypes = [
  'Staff Behavior',
  'Service Quality',
  'Billing Issue',
  'Wait Time',
  'Facilities',
  'Medical Care',
  'Appointment Issue',
  'Other',
];

export function ComplaintFormForm({
  title = 'File a Complaint',
  subtitle = 'We value your feedback. Please share your concerns so we can improve our services.',
  showNotice = true,
  onSuccess,
}) {
  const [form, setForm] = useState({
    patient_name: '',
    contact_number: '',
    email: '',
    complaint_type: '',
    severity: 'medium',
    description: '',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    injectStyles();
  }, []);

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: '' }));
    }
  }

  function validateForm() {
    const newErrors = {};

    if (!form.patient_name.trim()) {
      newErrors.patient_name = 'Name is required.';
    }

    if (!form.contact_number.trim()) {
      newErrors.contact_number = 'Contact number is required.';
    } else if (!/^[\d\s\-+()]{7,20}$/.test(form.contact_number)) {
      newErrors.contact_number = 'Please enter a valid phone number.';
    }

    if (!form.complaint_type) {
      newErrors.complaint_type = 'Please select a complaint type.';
    }

    if (!form.description.trim()) {
      newErrors.description = 'Description is required.';
    } else if (form.description.trim().length < 20) {
      newErrors.description = 'Please provide more details (at least 20 characters).';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!validateForm()) return;

    const submittedForm = { ...form };
    setSuccess(true);
    setForm({
      patient_name: '',
      contact_number: '',
      email: '',
      complaint_type: '',
      severity: 'medium',
      description: '',
    });
    onSuccess?.(submittedForm);
  }

  const severityOptions = [
    { value: 'low', label: 'Low', style: baseStyles.severityLow },
    { value: 'medium', label: 'Medium', style: baseStyles.severityMedium },
    { value: 'high', label: 'High', style: baseStyles.severityHigh },
    { value: 'critical', label: 'Critical', style: baseStyles.severityCritical },
  ];

  return (
    <div style={baseStyles.shell} className="complaint-shell">
      <div style={baseStyles.card} className="complaint-card">
        <div style={baseStyles.header}>
          <h2 style={baseStyles.title}>{title}</h2>
          <p style={baseStyles.subtitle}>{subtitle}</p>
        </div>

        <form style={baseStyles.form} onSubmit={handleSubmit}>
          {success ? (
            <div style={baseStyles.success} role="status" aria-live="polite">
              <div style={baseStyles.successIcon}>
                <i className="fas fa-check-circle" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Thank You!</h3>
              <p style={{ margin: 0, fontSize: 14 }}>
                Thank you, we will get back to you soon.
              </p>
              <button
                type="button"
                style={{ ...baseStyles.submitBtn, marginTop: 16, background: '#16a34a' }}
                onClick={() => setSuccess(false)}
              >
                Submit Another Complaint
              </button>
            </div>
          ) : (
            <>
              {showNotice && (
                <div style={baseStyles.notice}>
                  <i className="fas fa-info-circle" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>
                    Your complaint will be reviewed by our management team. For emergencies, please call our helpline immediately.
                  </span>
                </div>
              )}

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Your Name</label>
                <input
                  className="complaint-input"
                  style={{
                    ...baseStyles.input,
                    borderColor: errors.patient_name ? '#dc2626' : '#d1d5db',
                  }}
                  type="text"
                  value={form.patient_name}
                  onChange={(e) => updateField('patient_name', e.target.value)}
                  placeholder="Enter your full name"
                />
                {errors.patient_name && <div style={baseStyles.fieldError}>{errors.patient_name}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Contact Number</label>
                <input
                  className="complaint-input"
                  style={{
                    ...baseStyles.input,
                    borderColor: errors.contact_number ? '#dc2626' : '#d1d5db',
                  }}
                  type="tel"
                  value={form.contact_number}
                  onChange={(e) => updateField('contact_number', e.target.value)}
                  placeholder="Enter your phone number"
                />
                {errors.contact_number && <div style={baseStyles.fieldError}>{errors.contact_number}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Email (Optional)</label>
                <input
                  className="complaint-input"
                  style={baseStyles.input}
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="Enter your email (optional)"
                />
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Complaint Type</label>
                <select
                  className="complaint-select"
                  style={{
                    ...baseStyles.select,
                    borderColor: errors.complaint_type ? '#dc2626' : '#d1d5db',
                  }}
                  value={form.complaint_type}
                  onChange={(e) => updateField('complaint_type', e.target.value)}
                >
                  <option value="">Select complaint type</option>
                  {complaintTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.complaint_type && <div style={baseStyles.fieldError}>{errors.complaint_type}</div>}
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Severity Level</label>
                <div style={baseStyles.severityOptions} className="severity-options">
                  {severityOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`severity-btn ${form.severity === opt.value ? 'selected' : ''}`}
                      style={{
                        ...baseStyles.severityBtn,
                        ...opt.style,
                        ...(form.severity === opt.value ? { background: `${opt.style.borderColor}20`, borderWidth: 2 } : {}),
                      }}
                      onClick={() => updateField('severity', opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={baseStyles.field}>
                <label style={baseStyles.label}>Describe Your Complaint</label>
                <textarea
                  className="complaint-textarea"
                  style={{
                    ...baseStyles.textarea,
                    borderColor: errors.description ? '#dc2626' : '#d1d5db',
                  }}
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Please provide details about your complaint..."
                />
                {errors.description && <div style={baseStyles.fieldError}>{errors.description}</div>}
              </div>

              <button
                className="complaint-submit"
                type="submit"
                style={baseStyles.submitBtn}
              >
                <i className="fas fa-flag" />
                Submit Complaint
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default ComplaintFormForm;
